import { useState } from 'react';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { capitalize } from 'lodash';
import { ImportType } from 'types/domain';

type Format = 'csv' | 'xlsx';
interface DownloadImportTemplateModalProps extends Omit<AlertDialog.AlertDialogRootProps, 'type'> {
    type: ImportType;
}

const DownloadImportTemplateModal = (props: DownloadImportTemplateModalProps) => {
    const { type, children, ...rest } = props;
    const [format, setFormat] = useState<Format>('csv');
    const { accountId } = useCurrentAccount();
    const apiUrl = useAppState(state => state.apiUrl);

    const onClick = () => {
        window
            .open(
                `${apiUrl}/dashboard/accounts/${accountId}/import-template/${type}?format=${format}`,
                '_blank',
            )
            ?.focus();
    };

    return (
        <AlertDialog.Root {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header className={'flex items-center justify-between'}>
                        <div
                            className={
                                'flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500'
                            }
                        >
                            <FontAwesomeIcon icon={faDownload} className={'h-5 w-5'} />
                        </div>
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body className={'space-y-6'}>
                        <div className={'space-y-2'}>
                            <div>
                                <p
                                    className={
                                        'inline-block rounded-full bg-blue-50 px-2 text-base font-medium text-blue-600'
                                    }
                                >
                                    {displayType(type)}
                                </p>
                                <AlertDialog.Title className={'leading-10'}>
                                    Download Template
                                </AlertDialog.Title>
                            </div>
                            <AlertDialog.Description>
                                Download an import template formatted with the correct column
                                headers, including headers for your custom fields. Also includes a
                                sample row of data—make sure to remove it before importing.
                            </AlertDialog.Description>
                        </div>
                        <div className={'space-y-2'}>
                            <Label>Format</Label>
                            <RadioGroup.Root
                                defaultValue={format}
                                onValueChange={value => setFormat(value as Format)}
                                variant={'card'}
                            >
                                <RadioGroup.Item value={'csv'}>
                                    <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                        />
                                    </RadioGroup.Indicator>
                                    <Label
                                        className="group-radix-state-checked:text-blue-500"
                                        htmlFor="csv"
                                    >
                                        CSV
                                    </Label>
                                </RadioGroup.Item>
                                <RadioGroup.Item value={'xlsx'}>
                                    <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                        />
                                    </RadioGroup.Indicator>
                                    <Label
                                        className="group-radix-state-checked:text-blue-500"
                                        htmlFor="xlsx"
                                    >
                                        Excel
                                    </Label>
                                </RadioGroup.Item>
                            </RadioGroup.Root>
                        </div>

                        <div>
                            <Button
                                variant="solid"
                                type={'button'}
                                onClick={onClick}
                                className={'w-full'}
                            >
                                Download
                            </Button>
                        </div>
                    </AlertDialog.Body>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

function displayType(type: ImportType) {
    switch (type) {
        case 'contacts':
            return 'Contacts';
        case 'transactions':
            return 'Transactions';
        case 'transaction_plans':
            return 'Transaction Plans';
        case 'funds':
            return 'Funds';
        default:
            return capitalize(type);
    }
}

const DownloadImportTemplateModalTrigger = AlertDialog.Trigger;

const Root = DownloadImportTemplateModal;
const Trigger = DownloadImportTemplateModalTrigger;

export { Root, Trigger, DownloadImportTemplateModal, DownloadImportTemplateModalTrigger };
export type { DownloadImportTemplateModalProps };
